import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './Button';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.DEV) console.error('[racksavant]', error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div
        style={{
          minHeight: '60vh',
          display: 'grid',
          placeItems: 'center',
          padding: 'var(--rs-space-8)',
          textAlign: 'center',
        }}
        role="alert"
      >
        <div style={{ display: 'grid', gap: 'var(--rs-space-4)', maxWidth: 420 }}>
          <h2 className="rs-u-serif" style={{ fontSize: 'var(--rs-text-title)' }}>
            The lights went out for a second
          </h2>
          <p className="rs-u-muted" style={{ fontSize: 'var(--rs-text-body-sm)' }}>
            Nothing you made is lost. Try that again.
          </p>
          <div style={{ display: 'flex', gap: 'var(--rs-space-3)', justifyContent: 'center' }}>
            <Button variant="primary" onClick={this.reset}>
              Try again
            </Button>
            <Button onClick={() => window.location.assign('/app')}>Back to the rack</Button>
          </div>
        </div>
      </div>
    );
  }
}
